import { USER } from 'constants/esDataTypes';
import { GenericDataType } from 'modules/domains';
import { validateSearchQueries } from 'modules/queryBuilder/legacyQueryBuilder/helpers';
import { TRIGGER_METADATA, WORKFLOW_TRIGGERS } from 'modules/workflows/constants';
import {
  DataTypeSearchCombo,
  FieldMatchNodeData,
  SearchQueryCombo,
  SearchQueryField,
  StartNodeData,
} from 'modules/workflows/types';
import { ADDED_ITEM_TO_CART_FIELDS, CUSTOM_EVENT_FIELDS } from 'utils/fieldConstants';
import walkSearchQueries, { WalkSearchQueryQueryTypes } from '../../walkSearchQueries';
import {
  INDETERMINABLE_ORIGIN_TRIGGERS,
  TRIGGERS_WITH_MIXED_TOP_LEVEL,
  UNFILTERABLE_TRIGGERS,
  YesNoSearchQueryLevels,
} from './fieldMatchUtils.constants';

/**
 * Is the node data for fields match valid?
 */
export const isValid = (nodeData: FieldMatchNodeData): boolean => {
  const { searchQuery } = nodeData || {};
  const { searchQueries = [] } = searchQuery || {};

  if (!searchQueries?.length) return false;

  // @ts-ignore
  return !validateSearchQueries(searchQueries).length;
};

/**
 * Do the topmost dataTypes all match an expected data type?
 * @param {string} expectedDataType The expected data type.
 * @param {SearchQueryCombo[]} queries The queries to check. Type: {@link SearchQueryCombo|SearchQueryCombo[]}.
 */
const doTopLevelDataTypesMatch = (expectedDataType: string, queries: SearchQueryCombo[] = []) => {
  let queryIsValid = true;
  walkSearchQueries(queries, {
    callback: ({ query, type, stop }) => {
      if (type !== WalkSearchQueryQueryTypes.DataTypeCombo) return;
      queryIsValid = (query as DataTypeSearchCombo)?.dataType === expectedDataType;
      if (!queryIsValid) stop();
    },
    maxLevel: YesNoSearchQueryLevels.DataType,
  });

  return queryIsValid;
};

/**
 * Is this field associated with a particular event?
 *
 * It needs to exactly match the level to be associated.
 * As per [WF-4840](https://iterable.atlassian.net/browse/WF-4840), it also
 * needs to match the nested names.
 *
 * @param {string} field The field to check.
 * @param {string} event The event to check against.
 *
 * @example
 * // Simple
 * doesFieldContainEvent('test', 'test') // true
 * doesFieldContainEvent('test.field', 'test') // true
 * doesFieldContainEvent('mytest', 'test') // false
 * doesFieldContainEvent('myEvent.test', 'test') // false
 *
 * // Nested
 * doesFieldContainEvent('very.nested.object', 'very.nested.object') // true
 * doesFieldContainEvent('very.nested.object.field', 'very.nested.object') // true
 * doesFieldContainEvent('very.nested.objectButDiff', 'very.nested.object') // false
 * doesFieldContainEvent('very.nested', 'very.nested.object') // false
 */
const doesFieldContainEvent = (field = '', event = '') => {
  const eventSections = event.split('.');
  const fieldSections = field.split('.');

  for (let i = 0; i < eventSections.length; i++) {
    if (eventSections[i] !== fieldSections[i]) return false;
  }

  return true;
};

/**
 * Is each field in a custom event valid?
 * @param {string} eventName The name of the event.
 * @param {SearchQueryCombo[]} queries The queries to check. Type: {@link SearchQueryCombo|SearchQueryCombo[]}.
 * @param {string[]} defaultFields Fields which are always valid for the event.
 */
const areCustomEventFieldsValid = ({
  eventName,
  queries = [],
  defaultFields = [],
}: {
  eventName: string;
  queries: SearchQueryCombo[];
  defaultFields: string[];
}) => {
  let queryIsValid = true;

  walkSearchQueries(queries, {
    callback: ({ query, type, stop }) => {
      if (type !== WalkSearchQueryQueryTypes.Field) return;
      const field = (query as SearchQueryField)?.field;

      // if user hasn't selected the field yet, hold back on validation
      if (field) {
        queryIsValid = defaultFields.includes(field) || doesFieldContainEvent(field, eventName);
        if (!queryIsValid) stop();
      }
    },
    maxLevel: YesNoSearchQueryLevels.Field,
  });

  return queryIsValid;
};

const VALID_DATATYPE_FOR_TRIGGER = {
  [WORKFLOW_TRIGGERS.RECEIVED_API]: USER,
};

const areFieldsValidForTrigger = ({
  nodeClass,
  queries,
}: {
  nodeClass: string;
  queries: SearchQueryCombo[];
}) => {
  // valid here is when the data types of each query are valid.
  const validFieldDataType = VALID_DATATYPE_FOR_TRIGGER[nodeClass] || [];
  let queryIsValid = true;

  walkSearchQueries(queries, {
    callback: ({ query, type, stop }) => {
      if (type !== WalkSearchQueryQueryTypes.Field) return;
      const field = (query as SearchQueryField)?.field;
      const dataType = (query as SearchQueryField)?.dataType;

      // if user hasn't selected the field yet, hold back on validation
      if (field) {
        queryIsValid = dataType === validFieldDataType;
        if (!queryIsValid) stop();
      }
    },
    maxLevel: YesNoSearchQueryLevels.Field,
  });

  return queryIsValid;
};

/**
 * Is the field match node data valid for the selected trigger?
 *
 * The logic to determine validity is outlined in the following:
 * - [Lucidchart](https://lucid.app/lucidchart/47c89ecc-d757-4979-a734-dc1104aa0b7f/edit?viewport_loc=-728%2C-1172%2C3788%2C1713%2C0_0&invitationId=inv_ecbc603b-5585-46db-9efe-a6b5fa0eb966)
 * - [Slab doc](https://iterable.slab.com/posts/yes-no-split-node-data-client-side-validation-lov44gaf)
 *
 * @param {FieldMatchNodeData} fieldMatchNodeData The data for the fields match node.  Type: {@link FieldMatchNodeData}.
 * @param {StartNodeData} startNodeData The data for the start node.  Type: {@link StartNodeData}.
 */
export const isValidForTrigger = (
  fieldMatchNodeData: FieldMatchNodeData,
  startNodeData: StartNodeData,
) => {
  // always valid if we're not filtering by trigger event
  if (!fieldMatchNodeData.matchTriggeringEventFields) return true;

  const { nodeClass } = startNodeData;
  const { searchQuery } = fieldMatchNodeData;

  // if we are unable to filter on the trigger, this is invalid
  if (UNFILTERABLE_TRIGGERS.includes(nodeClass)) return false;

  // if we cannot determine the originating event, assume valid
  if (INDETERMINABLE_ORIGIN_TRIGGERS.includes(nodeClass)) return true;

  const searchQueries = (searchQuery?.searchQueries || []) as SearchQueryCombo[];
  const triggerMetadata = TRIGGER_METADATA[nodeClass];
  const correctTopLevelDataType =
    VALID_DATATYPE_FOR_TRIGGER[nodeClass] || triggerMetadata?.dataType || '';
  // Are all the selected data types the data type associated with the trigger?
  if (!doTopLevelDataTypesMatch(correctTopLevelDataType, searchQueries)) return false;

  if (TRIGGERS_WITH_MIXED_TOP_LEVEL.includes(nodeClass)) {
    return areFieldsValidForTrigger({
      nodeClass,
      queries: searchQueries,
    });
  }

  // If the data type is not a custom event, we're done
  if (correctTopLevelDataType !== GenericDataType.CustomEvent) return true;
  const isAddedItemToCart = nodeClass === WORKFLOW_TRIGGERS.ADDED_ITEM_TO_CART;
  // Use `updateCart` as the event name for the added item to cart trigger, as
  // per the comments in [WF-1835](https://iterable.atlassian.net/browse/WF-1835)
  const eventName = triggerMetadata?.triggerEventNames || startNodeData.eventName;

  // If the event name is blank, the fields match node is invalid
  if (!eventName) return false;

  return areCustomEventFieldsValid({
    eventName,
    queries: searchQueries,
    defaultFields: isAddedItemToCart ? ADDED_ITEM_TO_CART_FIELDS : CUSTOM_EVENT_FIELDS,
  });
};
